import React, { useState, useEffect } from 'react'
import { Alert, AlertTitle, Box, Typography, Grid } from '@mui/material'
import WeightZoneRulesBuilder from './WeightZoneRulesBuilder'
import Rules from '../../merchants/Rules'
import RuleHistory from './RuleHistory'
import MuiTabs from '../../../components/MuiTabs'
import PageTitle from '../../../components/PageTitle'
import { apiConstants, getGeneric } from '../../../common/constants'
import { useSnackbar } from 'notistack'
import { RHFSearchableSelectField, FormProvider } from '../../../common/hook-form'
import { useForm } from 'react-hook-form'
import {
  useGetAllActiveMerchantsIdAndNameCachedQuery,
  useGetMetchantConfigQuery,
} from '../../../redux/commonApiCalls'
import _ from 'lodash'
import Loader from '../../../common/Loader'
import StorefrontIcon from '@mui/icons-material/Storefront'
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined'
import TuneIcon from '@mui/icons-material/Tune'
import TimelineIcon from '@mui/icons-material/Timeline'
import HistoryIcon from '@mui/icons-material/History'
import {
  WrapperCard,
  TabContainer,
  EmptyStateContainer,
  EmptyStateIconStyled,
  EmptyStateText,
  RulesContainer,
  BuildTabContent,
} from './styled'

export default function WeightZoneRulesBuilderWrapper() {
  const [active, setActive] = useState(0)
  const [merchantData, setMerchantData] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const { enqueueSnackbar } = useSnackbar()
  const methods = useForm()
  const { watch } = methods

  const { data: merchants = [] } = useGetAllActiveMerchantsIdAndNameCachedQuery()
  const activeMerchants = merchants.filter((val) => val.is_deleted === '0')

  const tabHeaders = [
    { label: 'Existing Rules', value: 0 },
    { label: 'Build & Upload Rules', value: 1 },
    { label: 'Allocation Rules History', value: 2 },
  ]

  // Watch for changes to the merchant field
  const selectedMerchantId = watch('merchant')
  const { data: merchantConfig } = useGetMetchantConfigQuery(
    {
      id: selectedMerchantId,
    },
    { skip: !selectedMerchantId, refetchOnMountOrArgChange: true }
  )

  useEffect(() => {
    if (selectedMerchantId) {
      setIsLoading(true)
      getGeneric(`${apiConstants.MERCHANT}/${selectedMerchantId}`)
        .then((response) => {
          setMerchantData(response?.data)
          setIsLoading(false)
        })
        .catch((err) => {
          console.error(err)
          enqueueSnackbar('Error loading merchant details', {
            variant: 'error',
          })
          setIsLoading(false)
        })
    }
  }, [selectedMerchantId, enqueueSnackbar])

  return (
    <>
      {isLoading && <Loader />}

      <PageTitle>Predictive Allocation & Courier Engine</PageTitle>

      <FormProvider methods={methods}>
        <RHFSearchableSelectField
          sx={{ '& .MuiInputBase-root': { backgroundColor: '#fff' } }}
          name='merchant'
          label='Select Merchant'
          options={_.orderBy(activeMerchants, 'name').map((val) => ({
            value: val.id,
            label: val.companyName ? `${val.name}` : val.name,
          }))}
        />
      </FormProvider>

      {selectedMerchantId &&
        merchantConfig &&
        !merchantConfig.automatedCourierAllocationEnabled && (
          <Alert sx={{ mt: 2, mb: 1, borderRadius: 3 }} severity='info' variant='outlined'>
            <AlertTitle>Automated Allocation is not enabled</AlertTitle>
            This merchant is not enabled for Automated Allocation. Kindly activate the same first.
            The option to activate Automated Allocation is available at "ProShip Admin Panel {
              '>>'
            }{' '}
            Masters {'>>'} Merchants {'>>'} select Merchant {'>>'} enable.
          </Alert>
        )}

      {!selectedMerchantId && (
        <Box sx={{
          mt: 4, py: 6, px: 3,
          backgroundColor: '#fff',
          borderRadius: 3,
          border: '1px solid #e8e8e8',
          textAlign: 'center',
        }}>
          <Box sx={{
            width: 72, height: 72, borderRadius: '50%',
            backgroundColor: '#fff3e0', border: '1px solid #ffe0b2',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            mx: 'auto', mb: 2,
          }}>
            <RocketLaunchOutlinedIcon sx={{ fontSize: 36, color: '#e65100' }} />
          </Box>
          <Typography variant='h6' fontWeight={700} color='text.primary' sx={{ mb: 0.5 }}>
            PACE — Predictive Allocation & Courier Engine
          </Typography>
          <Typography variant='body2' color='text.secondary' sx={{ mb: 4, maxWidth: 480, mx: 'auto' }}>
            Select a merchant above to view existing allocation rules, build new ones, or review rule change history.
          </Typography>

          <Grid container spacing={2} sx={{ maxWidth: 600, mx: 'auto' }}>
            {[
              { icon: <TuneIcon sx={{ fontSize: 22, color: '#1565c0' }} />, bg: '#e3f2fd', title: 'Existing Rules', desc: 'View current courier allocation rules' },
              { icon: <TimelineIcon sx={{ fontSize: 22, color: '#2e7d32' }} />, bg: '#e8f5e9', title: 'Build & Upload', desc: 'Create or modify allocation rules' },
              { icon: <HistoryIcon sx={{ fontSize: 22, color: '#7b1fa2' }} />, bg: '#f3e5f5', title: 'Rule History', desc: 'Track changes to allocation rules' },
            ].map((item, i) => (
              <Grid item xs={12} sm={4} key={i}>
                <Box sx={{
                  p: 2, borderRadius: 2,
                  border: '1px solid #f0f0f0',
                  backgroundColor: '#fafafa',
                  textAlign: 'center',
                }}>
                  <Box sx={{
                    width: 40, height: 40, borderRadius: 1.5,
                    backgroundColor: item.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    mx: 'auto', mb: 1,
                  }}>
                    {item.icon}
                  </Box>
                  <Typography variant='caption' fontWeight={700} display='block' color='text.primary'>
                    {item.title}
                  </Typography>
                  <Typography variant='caption' color='text.secondary' sx={{ fontSize: '0.7rem' }}>
                    {item.desc}
                  </Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {selectedMerchantId && (
        <WrapperCard>
          <TabContainer>
            <MuiTabs tabs={tabHeaders} active={active} setActive={setActive} />
          </TabContainer>

          {active === 0 && (
            <>
              {merchantData ? (
                <RulesContainer>
                  <Rules data={merchantData} />
                </RulesContainer>
              ) : (
                <EmptyStateContainer>
                  <EmptyStateIconStyled>
                    <StorefrontIcon fontSize='inherit' />
                  </EmptyStateIconStyled>
                  <EmptyStateText variant='body1'>
                    Select a merchant to view existing rules
                  </EmptyStateText>
                </EmptyStateContainer>
              )}
            </>
          )}

          {active === 1 && (
            <BuildTabContent>
              <WeightZoneRulesBuilder
                merchantId={selectedMerchantId}
                merchantConfig={merchantConfig}
              />
            </BuildTabContent>
          )}

          {active === 2 && (
            <RuleHistory merchantOid={selectedMerchantId} />
          )}
        </WrapperCard>
      )}
    </>
  )
}
